import { Controller } from "@hotwired/stimulus"

// Mirrors the list's current filter controls into the "save preset" form as
// `filters[...]` fields. Filter inputs live in several forms on a list page
// (the dropdown, the search box, status pills), so values are collected from
// the whole document rather than from a single form.
//
// Visible controls win over the hidden mirror inputs other filter forms render:
// a key touched by a visible control is taken as-is, even when it is now empty,
// so clearing a field before saving actually drops it from the preset.
export default class extends Controller {
  sync() {
    this.clearGeneratedFields()

    const scalars = new Map()
    const arrays = new Map()
    const visibleKeys = new Set()

    const collect = (input, hidden) => {
      if (input.disabled || this.element.contains(input)) return

      const match = input.name.match(/^f\[([^\]]+)\](\[\])?$/)
      if (!match) return

      const [, key, isArray] = match
      if (hidden && visibleKeys.has(key)) return
      if (!hidden) visibleKeys.add(key)
      if ((input.type === "radio" || input.type === "checkbox") && !input.checked) return

      if (input instanceof HTMLSelectElement && input.multiple) {
        Array.from(input.selectedOptions)
          .map(option => option.value)
          .filter(Boolean)
          .forEach(value => this.pushArrayValue(arrays, key, value))
        return
      }

      const value = (input.value || "").trim()
      if (!value) return

      if (isArray) {
        this.pushArrayValue(arrays, key, value)
      } else {
        scalars.set(key, value)
      }
    }

    this.filterInputs(false).forEach(input => collect(input, false))
    this.filterInputs(true).forEach(input => collect(input, true))

    scalars.forEach((value, key) => this.appendHidden(`filters[${key}]`, value))
    arrays.forEach((values, key) => {
      values.forEach(value => this.appendHidden(`filters[${key}][]`, value))
    })
  }

  filterInputs(hidden) {
    const selector = hidden
      ? 'input[type="hidden"][name^="f["]'
      : 'input[name^="f["]:not([type="hidden"]), select[name^="f["], textarea[name^="f["]'

    return Array.from(document.querySelectorAll(selector))
  }

  pushArrayValue(arrays, key, value) {
    const values = arrays.get(key) || []
    if (!values.includes(value)) values.push(value)
    arrays.set(key, values)
  }

  appendHidden(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    input.dataset.filterPresetGenerated = "true"
    this.element.appendChild(input)
  }

  clearGeneratedFields() {
    this.element
      .querySelectorAll("[data-filter-preset-generated]")
      .forEach(field => field.remove())
  }
}
